import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import RichTextEditor from '../../components/admin/RichTextEditor';
import FileUploader from '../../components/admin/FileUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: [],
    featured_image: '',
    status: 'draft'
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/blog`, {
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(
          `${API_URL}/admin/blog/${editingPost.post_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Post atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/admin/blog`, formData, {
          withCredentials: true
        });
        toast.success('Post criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadPosts();
    } catch (error) {
      toast.error('Erro ao salvar post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      tags: post.tags,
      featured_image: post.featured_image || '',
      status: post.status
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Tem certeza que deseja excluir este post?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/blog/${postId}`, {
        withCredentials: true
      });
      toast.success('Post excluído com sucesso!');
      loadPosts();
    } catch (error) {
      toast.error('Erro ao excluir post');
    }
  };

  const handlePublish = async (postId) => {
    try {
      await axios.put(
        `${API_URL}/admin/blog/${postId}`,
        { status: 'published' },
        { withCredentials: true }
      );
      toast.success('Post publicado com sucesso!');
      loadPosts();
    } catch (error) {
      toast.error('Erro ao publicar post');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      tags: [],
      featured_image: '',
      status: 'draft'
    });
    setEditingPost(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold mb-2">Blog</h2>
          <p className="text-neutral-600">Gerencie os artigos do blog</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="flex items-center gap-2"
        >
          <iconify-icon icon="solar:document-add-linear"></iconify-icon>
          Novo Post
        </Button>
      </div>

      {/* Posts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <div
            key={post.post_id}
            className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-lg transition-shadow"
          >
            {post.featured_image && (
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono uppercase tracking-wider text-neutral-500">
                  {post.category}
                </span>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    post.status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {post.status === 'published' ? 'Publicado' : 'Rascunho'}
                </span>
              </div>
              <h3 className="font-bold text-lg mb-2 line-clamp-2">{post.title}</h3>
              <p className="text-sm text-neutral-600 mb-4 line-clamp-3">{post.excerpt}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(post)}
                  className="flex-1 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 rounded-lg text-sm font-medium transition-colors"
                >
                  Editar
                </button>
                {post.status === 'draft' && (
                  <button
                    onClick={() => handlePublish(post.post_id)}
                    className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Publicar
                  </button>
                )}
                <button
                  onClick={() => handleDelete(post.post_id)}
                  className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                >
                  <iconify-icon icon="solar:trash-bin-linear" className="text-xl"></iconify-icon>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12 text-neutral-500">
          <iconify-icon icon="solar:document-linear" className="text-6xl mb-4"></iconify-icon>
          <p>Nenhum post criado ainda</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Editar Post' : 'Novo Post'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="excerpt">Resumo *</Label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                className="w-full border border-neutral-300 rounded-md p-2 min-h-[80px]"
                required
              />
            </div>
            <div>
              <Label>Conteúdo *</Label>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => setFormData({ ...formData, content: html })}
                folder="blog"
                placeholder="Escreva o artigo aqui. Cole ou arraste imagens diretamente."
              />
            </div>
            <div>
              <Label htmlFor="category">Categoria *</Label>
              <Input
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label>Imagem Destacada</Label>
              <FileUploader
                value={formData.featured_image}
                onChange={(url) => setFormData({ ...formData, featured_image: url })}
                accept="image"
                folder="blog"
                testId="blog-featured-uploader"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingPost ? 'Atualizar' : 'Criar Rascunho'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
